import { StyledBreadcrumb } from "@aureskonnect/react-ui";
import PageTitle from "@components/Common/PageTitle";
import MessageInfo from "@components/Common/MessageInfo";
import { DynamicTable } from "@maherunlocker/custom-react-table";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";
import { ToastContainer } from "react-toastify";
import { store as useStore } from "@components/VerticalLayout/store";
import { store, setGlobalDataIsUpdated, setModify, setDuration } from "./store";
import { ModalContentWrapper } from "./ModalContentWrapper";
import { ModalWrapper } from "./ModalWrapper";
import { useLocation } from "react-router-dom";
import { getPathAuthorization } from "@components/VerticalLayout";
import { useSelector } from "react-redux";
import ApkModal from "./ApkModal";
import NProgressWrapper from "@components/Common/NProgressWrapper";
import { createCCSetupBreadcrumb, createCCSetupColumns } from "./CheckIcon";
import { useAuthHeader } from "../../../../api/headersKeycloak";

export default function CCSetup() {
    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";
    const { t } = useTranslation();
    const getAuthHeader = useAuthHeader();
    const location = useLocation();
    const selectedAccount = useSelector(
        (state: any) => state.global.selectedAccount
    );
    const authorizationList = useSelector(
        (state: any) => state.global.infoUser.profileAuthorization
    );
    const {
        isModalOpened,
        isModalApkOpened,
        globalDataIsUpdated,
        key,
        iuudApk,
    } = useSnapshot(store);
    const [filterActive, setLocalFilterActive] = React.useState<boolean>(false);
    const [selectedRows, setSelectedRows] = React.useState<any>([]);
    const [mergedData, setMergedData] = React.useState<any>(null);
    const [isFetchingData, setIsFetchingData] = React.useState(true);
    const { shopID, franchiseID, oneShop } = useSnapshot(useStore);

    const urlApi = `${process.env.REACT_APP_API_V2_URL}/settings/list/c&c/V2?&shopId=${shopID}&franchiseId=${franchiseID}`;

    const arrayOfCustomColumns = useMemo(
        () =>
            createCCSetupColumns({
                t,
                setSelectedRows,
                setMergedData,
                setIsFetchingData,
            }),
        [t]
    );
    const breadcrumbItems = useMemo(
        () => createCCSetupBreadcrumb({ t, selectedAccount, oneShop }),
        [t, selectedAccount, oneShop]
    );

    React.useEffect(() => {
        const authorization = getPathAuthorization(
            location.pathname,
            authorizationList
        );
        setModify(authorization.Modifie);
    }, [location.pathname, authorizationList]);

    React.useEffect(() => {
        if (i18nextLng === "fr" || i18nextLng === "en") {
            setGlobalDataIsUpdated(true);
        }
    }, [i18nextLng]);

    React.useEffect(() => {
        setDuration("");
    }, []);

    const hasSelectedRows = selectedRows.length > 0;
    let showApkModal = isModalApkOpened && selectedRows.length > 0;
    const showConfigModal =
        isModalOpened && hasSelectedRows && !!iuudApk && !!mergedData;
    const selectedApkName =
        selectedRows[0]?.iuudApk?.find((a: any) => a.iuud === iuudApk)?.nom || "";
    return (
        <React.Fragment>
            <PageTitle title={t("Applications")} />

            <div className="page-content__clz">
                <StyledBreadcrumb items={breadcrumbItems} />
                <MessageInfo
                    message={t(
                        "To configure the C&C application and website, it is crucial to consider the sales methods and payment modes associated with the C&C site and application, as well as the VAT rates, in order to configure them appropriately."
                    )}
                />

                <div
                    style={{
                        display: "grid",
                        minHeight: "650px",
                        gridTemplateColumns: "auto",
                        gridColumnGap: "4px",
                        border: 0,
                    }}
                >
                    <DynamicTable
                        key={key}
                        url={urlApi}
                        name="Choice"
                        showFilter
                        canSelect={true}
                        setLocalFilterActive={setLocalFilterActive}
                        filterActive={filterActive}
                        actionColumn={() => <></>}
                        arrayOfCustomColumns={arrayOfCustomColumns}
                        requestHeader={{
                            "Accept-Language": i18nextLng,
                            ...getAuthHeader,
                        }}
                        showGlobalFilter
                        canExpand
                        elevationTable={1}
                        minHeight="500px"
                        maxHeight="500px"
                        setDataIsUpdated={
                            setGlobalDataIsUpdated as
                                | React.Dispatch<
                                      React.SetStateAction<number | boolean>
                                  >
                                | undefined
                        }
                        dataIsUpdated={globalDataIsUpdated}
                        setSelectedRows={setSelectedRows}
                    />
                    {showApkModal && (
                        <ApkModal
                            selectedRow={selectedRows[0]}
                            setIsFetchingData={setIsFetchingData}
                            setMergedData={setMergedData}
                        />
                    )}

                    {showConfigModal && (
                        <ModalWrapper
                            title={[
                                t("C&C Setup"),
                                !oneShop && selectedRows[0]?.Boutique ? selectedRows[0].Boutique : "",
                                selectedApkName,
                            ].filter(Boolean).join(" - ")}
                            selectedRows={selectedRows}
                        >
                            {mergedData && (
                                <ModalContentWrapper
                                    selectedRows={[mergedData]}
                                />
                            )}
                        </ModalWrapper>
                    )}
                    <ToastContainer limit={1} />
                    {!isFetchingData && (
                        <NProgressWrapper isLoading={isFetchingData} />
                    )}
                </div>
            </div>
        </React.Fragment>
    );
}
